import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';

export type ThemeMode = 'light' | 'dark';
export type AccentColor = 'orange' | 'blue' | 'green' | 'slate';

// Keep legacy Theme type for backwards compat
export type Theme = 'monochrome' | 'colorful';

const MODE_KEY = 'xrank-theme-mode';
const ACCENT_KEY = 'xrank-accent-color';
// Keep old key for migration
const LEGACY_KEY = 'resume-tailor-theme';

interface ThemeContextType {
  theme: Theme;
  mode: ThemeMode;
  accent: AccentColor;
  toggleTheme: () => void;
  setTheme: (theme: Theme) => void;
  setMode: (mode: ThemeMode) => void;
  setAccent: (accent: AccentColor) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

interface ThemeProviderProps {
  children: ReactNode;
}

const VALID_MODES: ThemeMode[] = ['light', 'dark'];
const VALID_ACCENTS: AccentColor[] = ['orange', 'blue', 'green', 'slate'];

function getInitialMode(): ThemeMode {
  try {
    const stored = localStorage.getItem(MODE_KEY);
    if (stored && VALID_MODES.includes(stored as ThemeMode)) return stored as ThemeMode;
    // Migrate from legacy
    const legacy = localStorage.getItem(LEGACY_KEY);
    if (legacy === 'colorful') return 'dark';
  } catch { /* noop */ }
  return 'light';
}

function getInitialAccent(): AccentColor {
  try {
    const stored = localStorage.getItem(ACCENT_KEY);
    if (stored && VALID_ACCENTS.includes(stored as AccentColor)) return stored as AccentColor;
  } catch { /* noop */ }
  return 'orange';
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const [mode, setModeState] = useState<ThemeMode>(getInitialMode);
  const [accent, setAccentState] = useState<AccentColor>(getInitialAccent);

  useEffect(() => {
    // Apply both mode and accent as data attributes
    document.documentElement.setAttribute('data-mode', mode);
    document.documentElement.setAttribute('data-accent', accent);
    // Legacy: keep data-theme for any CSS still referencing it
    document.documentElement.setAttribute('data-theme', mode === 'dark' ? 'colorful' : 'monochrome');
    localStorage.setItem(MODE_KEY, mode);
    localStorage.setItem(ACCENT_KEY, accent);
  }, [mode, accent]);

  // Legacy compat
  const theme: Theme = mode === 'dark' ? 'colorful' : 'monochrome';
  const toggleTheme = () => setModeState(prev => prev === 'light' ? 'dark' : 'light');
  const setTheme = (t: Theme) => setModeState(t === 'colorful' ? 'dark' : 'light');
  const setMode = (m: ThemeMode) => setModeState(m);
  const setAccent = (a: AccentColor) => setAccentState(a);

  const value: ThemeContextType = { theme, mode, accent, toggleTheme, setTheme, setMode, setAccent };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

// Helper hook for theme-aware styling
export const useThemeStyles = () => {
  const { mode } = useTheme();
  
  return {
    isDark: mode === 'dark',
    isLight: mode === 'light',
    isMonochrome: mode === 'light',
    isColorful: mode === 'dark',
    getThemeClass: (monoClass: string, colorfulClass: string) => 
      mode === 'dark' ? colorfulClass : monoClass,
  };
};